import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { api, formatRelative } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Paperclip, Send, Trash2, AtSign, X } from "lucide-react";

const MAX_FILES = 5;
const MAX_BYTES = 6 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp";

// Composer text style — applied IDENTICALLY to both the overlay div and the
// underlying Textarea so the caret stays aligned with the visible chip on
// every line. Do NOT change one without the other. We use inline style so the
// values beat Tailwind's `.text-sm` (which bundles line-height:1.25rem) which
// previously won specificity over `.leading-6` on the Textarea.
const COMPOSER_TEXT_STYLE = {
  fontSize: "14px",
  lineHeight: "24px",
  padding: "8px 0",
  fontFamily: "inherit",
  fontWeight: 400,
};

/**
 * Comments — Spotify-style threaded conversation panel.
 * Avatar initial bubble, message body, attachments grid, @mention autocomplete.
 *
 * Mentions are stored inline as: @[Display Name](user:<id>)
 * For display we render them as a chip; mentioned user-ids are also sent
 * separately so the backend doesn't have to re-parse.
 */
export const CommentsPanel = ({ recordId, canComment = true, onCountChange }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  // Mentions: list of { name, id } the user has explicitly picked. Visible in
  // the composer as "@Name " plain text (no token markup) — the overlay div
  // highlights them as chips, and we re-serialise to @[Name](user:id) at submit.
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [files, setFiles] = useState([]); // File[]
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mentionUsers, setMentionUsers] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [caret, setCaret] = useState(0);
  const textareaRef = useRef(null);
  const overlayRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/bath-records/${recordId}/comments`);
      const list = r.data || [];
      setComments(list);
      if (onCountChange) onCountChange(list.length);
    } catch (e) {
      // 403 for third-party on other records — silent
    } finally {
      setLoading(false);
    }
  }, [recordId, onCountChange]);

  useEffect(() => {
    load();
    api.get("/users/mention-list").then((r) => setMentionUsers(r.data || [])).catch(() => {});
  }, [load]);

  // ── Mention autocomplete ──────────────────────────────────────────────
  const onBodyChange = (e) => {
    const v = e.target.value;
    setBody(v);
    const pos = e.target.selectionStart || v.length;
    setCaret(pos);
    // Look backwards from caret for @
    const upto = v.slice(0, pos);
    const m = upto.match(/@([\w]*)$/);
    if (m) {
      setMentionOpen(true);
      setMentionQuery(m[1].toLowerCase());
    } else {
      setMentionOpen(false);
      setMentionQuery("");
    }
  };

  const candidates = useMemo(() => {
    if (!mentionOpen) return [];
    const q = mentionQuery;
    const pool = mentionUsers.filter((u) => u.id !== user?.id);
    if (!q) return pool.slice(0, 6);
    return pool
      .filter((u) => (u.name || "").toLowerCase().includes(q) || (u.username || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionOpen, mentionQuery, mentionUsers, user?.id]);

  const insertMention = (u) => {
    // Insert just "@Name " in the textarea (plain text — no token markup).
    // We track {name, id} in mentionedUsers so the overlay can render a chip
    // and the submit step can re-serialise to @[Name](user:id) tokens.
    const before = body.slice(0, caret).replace(/@([\w]*)$/, "");
    const after = body.slice(caret);
    const visible = `@${u.name} `;
    const next = `${before}${visible}${after}`;
    setBody(next);
    setMentionedUsers((prev) =>
      prev.some((x) => x.id === u.id) ? prev : [...prev, { name: u.name, id: u.id }]
    );
    setMentionOpen(false);
    setMentionQuery("");
    setTimeout(() => {
      const el = textareaRef.current;
      if (el) {
        const newCaret = before.length + visible.length;
        el.focus();
        el.setSelectionRange(newCaret, newCaret);
        setCaret(newCaret);
      }
    }, 0);
  };

  // ── Attachments ───────────────────────────────────────────────────────
  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    const accepted = [];
    for (const f of list) {
      if (!ACCEPT.split(",").includes(f.type)) {
        toast.error(`${f.name}: only JPEG/PNG/WebP`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: max 6MB per file`);
        continue;
      }
      accepted.push(f);
    }
    setFiles((prev) => [...prev, ...accepted].slice(0, MAX_FILES));
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  // ── Submit ────────────────────────────────────────────────────────────
  const submit = async () => {
    const trimmed = body.trim();
    if (!trimmed && files.length === 0) return;
    setBusy(true);
    try {
      // Re-serialise visible "@Name" tags into the canonical
      // @[Name](user:id) token format the backend expects. Only mentions
      // whose name is still present in the body are sent.
      let serialised = trimmed;
      const idsInUse = [];
      for (const u of mentionedUsers) {
        const escaped = u.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const re = new RegExp(`@${escaped}(?=\\s|$|[^\\w])`, "g");
        if (re.test(serialised)) {
          serialised = serialised.replace(re, `@[${u.name}](user:${u.id})`);
          idsInUse.push(u.id);
        }
      }
      const fd = new FormData();
      fd.append("body", serialised);
      fd.append("mentions", idsInUse.join(","));
      files.forEach((f) => fd.append("files", f, f.name));
      await api.post(`/bath-records/${recordId}/comments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBody("");
      setMentionedUsers([]);
      setFiles([]);
      await load();
      toast.success("Comment posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post comment");
    } finally {
      setBusy(false);
    }
  };

  const removeComment = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/bath-records/${recordId}/comments/${c.id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
    if (e.key === "Escape" && mentionOpen) {
      setMentionOpen(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="comments-panel">
      {/* Composer */}
      {canComment && (
        <div className="rounded-lg border border-zinc-200 bg-white">
          <div className="flex gap-3 p-3">
            <div className="h-8 w-8 shrink-0 rounded-full bg-zinc-900 text-white text-xs font-bold flex items-center justify-center" aria-hidden>
              {initials(user?.name)}
            </div>
            <div className="flex-1 min-w-0 relative">
              <div className="relative">
                {/* Overlay div — mirrors the textarea text and renders tracked
                    @Name mentions as highlighted chips. Sits underneath the
                    transparent textarea so the user sees chips while typing.
                    Font, leading, padding MUST match the textarea exactly so
                    the caret stays aligned with the visible chip. Inline style
                    overrides Tailwind's `.text-sm` (which bundles line-height
                    1.25rem) — keep this in sync with the Textarea below. */}
                <div
                  ref={overlayRef}
                  aria-hidden
                  className="absolute inset-0 px-0 whitespace-pre-wrap break-words pointer-events-none select-none overflow-hidden text-zinc-900"
                  style={COMPOSER_TEXT_STYLE}
                  data-testid="composer-overlay"
                >
                  {renderComposerOverlay(body, mentionedUsers)}
                </div>
                <Textarea
                  ref={textareaRef}
                  value={body}
                  onChange={onBodyChange}
                  onKeyDown={onKeyDown}
                  onScroll={(e) => {
                    if (overlayRef.current) overlayRef.current.scrollTop = e.target.scrollTop;
                  }}
                  placeholder="Add a comment… type @ to mention someone. ⌘/Ctrl-Enter to send."
                  rows={3}
                  className="relative resize-none border-0 shadow-none focus-visible:ring-0 px-0 placeholder:text-zinc-400 bg-transparent text-transparent caret-zinc-900"
                  style={COMPOSER_TEXT_STYLE}
                  data-testid="comment-body"
                />
              </div>
              {mentionOpen && candidates.length > 0 && (
                <div className="absolute left-0 top-full mt-1 z-50 w-72 max-h-64 overflow-auto rounded-md border border-zinc-200 bg-white shadow-xl" data-testid="mention-suggestions">
                  {candidates.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 text-left"
                      data-testid={`mention-pick-${u.id}`}
                    >
                      <span className="h-6 w-6 rounded-full bg-zinc-200 text-zinc-700 text-[10px] font-bold flex items-center justify-center">
                        {u.initials}
                      </span>
                      <span className="text-sm text-zinc-900 truncate">{u.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-zinc-400 ml-auto">{u.role}</span>
                    </button>
                  ))}
                </div>
              )}

              {files.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <div key={i} className="relative h-16 w-16 rounded border border-zinc-200 overflow-hidden bg-zinc-50">
                      <img src={URL.createObjectURL(f)} alt={f.name} className="h-full w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/60 text-white flex items-center justify-center"
                        data-testid={`remove-attachment-${i}`}
                        aria-label="Remove attachment"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between px-3 pb-3 -mt-1">
            <div className="flex items-center gap-1">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                multiple
                hidden
                onChange={onPickFiles}
                data-testid="comment-file-input"
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 text-zinc-500 hover:text-zinc-900"
                onClick={() => fileInputRef.current?.click()}
                disabled={files.length >= MAX_FILES}
                data-testid="comment-attach"
              >
                <Paperclip className="h-3.5 w-3.5 mr-1" /> Attach
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 text-zinc-500 hover:text-zinc-900"
                onClick={() => {
                  const el = textareaRef.current;
                  if (!el) return;
                  const pos = el.selectionStart || body.length;
                  const next = body.slice(0, pos) + "@" + body.slice(pos);
                  setBody(next);
                  setCaret(pos + 1);
                  setMentionOpen(true);
                  setMentionQuery("");
                  setTimeout(() => { el.focus(); el.setSelectionRange(pos + 1, pos + 1); }, 0);
                }}
                data-testid="comment-mention"
              >
                <AtSign className="h-3.5 w-3.5 mr-1" /> Mention
              </Button>
            </div>
            <Button
              size="sm"
              onClick={submit}
              disabled={busy || (!body.trim() && files.length === 0)}
              className="bg-zinc-950 hover:bg-zinc-800 text-white h-8"
              data-testid="comment-submit"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {busy ? "Posting…" : "Comment"}
            </Button>
          </div>
        </div>
      )}

      {/* Comments list */}
      <div className="space-y-3">
        {loading && <p className="text-xs text-zinc-400">Loading comments…</p>}
        {!loading && comments.length === 0 && (
          <p className="text-xs text-zinc-400 flex items-center gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" />
            No comments yet — start the conversation.
          </p>
        )}
        {comments.map((c) => (
          <CommentBubble
            key={c.id}
            comment={c}
            currentUserId={user?.id}
            isAdmin={user?.role === "admin"}
            recordId={recordId}
            onDelete={() => removeComment(c)}
          />
        ))}
      </div>
    </div>
  );
};

const CommentBubble = ({ comment, currentUserId, isAdmin, recordId, onDelete }) => {
  const canDelete = comment.user_id === currentUserId || isAdmin;

  return (
    <div className="flex gap-3" data-testid={`comment-${comment.id}`}>
      <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-950 text-white text-xs font-bold flex items-center justify-center">
        {comment.user_initials || initials(comment.user_name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-zinc-950">{comment.user_name}</span>
          <span className="text-[11px] text-zinc-400">{formatRelative(comment.created_at)}</span>
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="ml-auto text-zinc-300 hover:text-rose-600 transition-colors"
              data-testid={`delete-comment-${comment.id}`}
              aria-label="Delete comment"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {comment.body && (
          <div className="mt-0.5 text-sm text-zinc-800 whitespace-pre-wrap break-words leading-relaxed">
            {renderBody(comment.body)}
          </div>
        )}
        {(comment.attachments || []).length > 0 && (
          <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-w-md">
            {comment.attachments.map((a, idx) => (
              <AuthedImg key={idx} recordId={recordId} commentId={comment.id} index={idx} name={a.name} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Renders an image via authed XHR → blob URL (since `<img>` can't send Authorization header).
const AuthedImg = ({ recordId, commentId, index, name }) => {
  const [src, setSrc] = useState(null);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    api
      .get(`/bath-records/${recordId}/comments/${commentId}/attachments/${index}`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setSrc(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [recordId, commentId, index]);

  if (!src) {
    return <div className="aspect-square rounded border border-zinc-200 bg-zinc-100 animate-pulse" />;
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setZoom(true)}
        className="aspect-square rounded border border-zinc-200 overflow-hidden bg-zinc-50 group"
        title={name}
        data-testid={`comment-attachment-${commentId}-${index}`}
      >
        <img src={src} alt={name} className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
      </button>
      {zoom && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setZoom(false)}
        >
          <img src={src} alt={name} className="max-h-[92vh] max-w-[92vw] object-contain" />
          <button
            type="button"
            onClick={() => setZoom(false)}
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center"
            aria-label="Close preview"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </>
  );
};

// Render posted-comment body. Mentions are stored inline as @[Name](user:<id>).
function renderBody(text) {
  const re = /@\[([^\]]+)\]\(user:([^\)]+)\)/g;
  const out = [];
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) {
      out.push(<span key={key++}>{text.slice(lastIdx, m.index)}</span>);
    }
    out.push(
      <span
        key={key++}
        className="inline-flex items-center rounded bg-zinc-900 text-white text-[12px] font-semibold px-1.5 py-0.5 mx-0.5"
      >
        @{m[1]}
      </span>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) out.push(<span key={key++}>{text.slice(lastIdx)}</span>);
  return out;
}

// Composer overlay — same plain text as the textarea but tracked @Name spans
// are background-highlighted. No padding, so widths match the textarea text
// 1:1 and the caret stays aligned with what the user sees.
function renderComposerOverlay(text, mentionedUsers) {
  if (!text) return null;
  if (!mentionedUsers || mentionedUsers.length === 0) {
    return <span>{text}</span>;
  }
  const escaped = mentionedUsers
    .map((u) => u.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  const re = new RegExp(`@(?:${escaped})(?=\\s|$|[^\\w])`, "g");
  const out = [];
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) out.push(<span key={key++}>{text.slice(lastIdx, m.index)}</span>);
    out.push(
      <span
        key={key++}
        className="bg-zinc-900 text-white rounded-sm"
        style={{ boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" }}
      >
        {m[0]}
      </span>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) out.push(<span key={key++}>{text.slice(lastIdx)}</span>);
  return out;
}

function initials(name) {
  const parts = (name || "").trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
